import { fabric } from 'fabric';
import { DEFAULT_FONT_SIZE } from '../objects/EmbeddedTextBehaviorsV2';
import Tool, {
  type IToolActionInput,
  type IToolAvailableInput,
  type IToolEnabledInput,
  simpleToolButton,
} from './Tool';
import { isSmartObject } from '../objects/guards';
import type SmartObject from '../objects/SmartObject';

// Convert a smart object into simple objects that can then be edited individually.
class ToolExplodeSmartObject extends Tool {
  static toolName = 'Convert to shapes';

  static action({ controller, selection }: IToolActionInput) {
    const newObjects: fabric.Object[] = [];

    // We need to transform the sub-objects to be in the right position.
    selection.forEach((obj: SmartObject) => {
      const scale = obj.scaleX;
      const leftOffset = (-obj.width * obj.scaleX) / 2;
      const topOffset = (-obj.height * obj.scaleY) / 2;

      const groupMatrix = obj.calcOwnMatrix();

      obj.objects.forEach(child => {
        // NOTE: Apply offsets before group transformation to handle rotation correctly
        child.left += leftOffset;
        child.top += topOffset;

        // @ts-expect-error missing from fabric types
        fabric.util.addTransformToObject(child, groupMatrix);

        // Scale the font size and stroke.
        // This is a kludge to deal with the fact that the smart object contents
        // are scaled by the smart object's scale when rendering, but when we
        // create standalone objects their properties are not scaled.
        child.fontSize = (child.fontSize || DEFAULT_FONT_SIZE) * scale;
        child.strokeWidth *= scale;

        obj.canvas.add(child);
        child.created(controller);
        child.fire('created');

        newObjects.push(child);
      });

      // Synchronize connectors once all objects are created.
      newObjects.forEach(obj => {
        obj.synchronize();
      });

      if (obj.deleted) {
        obj.deleted(controller);
      }
      obj.fire('deleted');
    });

    // Select the new objects.
    controller.canvas.discardActiveObject();
    if (newObjects.length === 1) {
      controller.canvas.setActiveObject(newObjects[0]);
    } else {
      const sel = new fabric.ActiveSelection(newObjects, {
        canvas: controller.canvas,
      });
      // @ts-expect-error it is actually a SimpleSelection but importing that here causes a circular dependency
      controller.canvas.setActiveObject(sel);
    }

    this.toolActionComplete({ controller });
  }

  static enabled({ selection }: IToolEnabledInput) {
    return !!selection?.getObjects().every(isSmartObject);
  }

  static available({ controller, selection }: IToolAvailableInput) {
    return this.enabled({ controller, selection });
  }

  static buttonComponent() {
    return simpleToolButton(ToolExplodeSmartObject, 'fa-regular fa-burst');
  }
}

export default ToolExplodeSmartObject;
